import React, { forwardRef, useRef } from 'react';
import classNames from 'classnames';
import Button from '@material-ui/core/Button';
import Popper from '@material-ui/core/Popper';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import {
  FDIcon, FDList, FDListItem, FDListItemAvatar, FDListItemSecondary, FDListItemText, FDPaper,
} from '@facet-wealth/design';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { useNavButtonsContext } from 'Components/ClientApp/CombinedApp/Navigation/NavButtonsContext';
import Badge from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Badge';
import useMobileLayout from 'Hooks/useMobileLayout';
import { useLayoutContext } from 'Context/LayoutContext';
import { getBorder, getBorderRadius } from 'Components/utils';
import throwInDevOutsideReact from 'Helpers/throwInDevOutsideReact';
import { useBodyBackgroundColor } from 'Hooks/useBodyBackgroundColor';

const useStyles = buildUseStyles('NavButton', (theme) => ({
  root: {
    display: 'flex',
    justifyContent: 'flex-start',
    color: theme.palette.facet.oceana800,
    padding: theme.spacing(2),
    borderRadius: theme.radius.none,
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.h4.fontSize,
    '&:hover': {
      textDecoration: 'underline',
      backgroundColor: ({ navColor }) => navColor,
    },
  },
  rootDesktop: {
    display: 'inline-flex',
    justifyContent: 'center',
    height: '100%',
  },
  // Necessary to keep the button in its hover state while the user's mouse is actually over the dropdown items, not the button
  forceHoverLook: {},
  expansionIcon: {
    marginRight: '-5px', // The icon comes with 5px of spacing on either side of it
    // When opening/closing, have the dropdown icon rotate 180 degrees with a transition
    transition: theme.transitions.create('transform', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.shorter,
    }),
  },
  popperContainer: {
    '& .FD-MuiPaper-root': {
      minWidth: '300px',
      maxHeight: `calc(100vh - ${theme.heights.desktopTopNavigationHeight}px - ${theme.spacing(3)}px)`,
      overflowY: 'auto',
      '@supports (-moz-appearance:none)': {
        scrollbarWidth: 'thin',
        scrollbarColor: `${theme.palette.facet.stone600} ${theme.palette.facet.stone50}`,
      },
      '&::-webkit-scrollbar': {
        width: theme.spacing(4),
        ...getBorderRadius(theme.radius.md, { bottomRight: true, topRight: true }),
        ...getBorder(theme, { left: true, color: theme.palette.facet.stone400 }),
      },
      '&::-webkit-scrollbar-track': {
        padding: theme.spacing(2),
      },
      '&::-webkit-scrollbar-thumb': {
        background: theme.palette.facet.stone650,
        ...getBorder(theme, { all: true, width: 13, color: 'transparent' }),
        ...getBorderRadius(theme.radius.xl, { all: true }),
        backgroundClip: 'content-box',
      },
      '&::-webkit-scrollbar-thumb:hover': {
        background: theme.palette.facet.stone700,
        ...getBorder(theme, { all: true, width: 13, color: 'transparent' }),
        ...getBorderRadius(theme.radius.xl, { all: true }),
        backgroundClip: 'content-box',
      },
      '&::-webkit-scrollbar-button:start': {
        display: 'none',
      },
      '&::-webkit-scrollbar-button:end': {
        display: 'none',
      },
    },
    display: 'flex', // allows popperShadow to take up the full height
    // Prevent badges (like in Messages), from appearing on top of the popper's contents
    zIndex: theme.zIndex.appBar,
  },
  badge: {
    // Give spacing between the badge and the text.
    // Can't use a Box over here, because if the badge isn't rendered (because there are 0 messages), then that would add unwanted spacing
    marginLeft: theme.spacing(1),
    display: 'block',
    textDecoration: 'none',
  },
  flip: {
    transform: 'rotate(180deg)',
  },
  dropdownItems: {
    // Remove borderRadius from every list item where it is - on the inside.
    // The inside position won't work for us because of where we're putting the background, but also, we don't want a border radius anywhere except at the bottom of the list
    '& .FD-MuiListItem-root .FD-MuiListItemButton-root': {
      ...getBorderRadius(theme.radius.none, { all: true }),
    },
    // FDListItem is designed to work well when you have a ton with images or 0 with images.
    // It just so happens that when you have our exact design (1 list item with an image at the very top, and then no images on any of the others), it looks funky.
    // We can't change the implementation in FD, because if we did, and we rendered it this way, but then the user clicked a button and we decided to render some more list items with images, we'd change the way all of the list items were rendered - that would look janky.
    // So we're overriding this exact case where we know things won't change
    '& .FD-MuiListItem-root .FD-MuiListItemIcon-root': {
      minWidth: 'unset',
      // On mobile there's not enough spacing to the left, ont desktop there's not enough spacing to the right
      [theme.breakpoints.down('sm')]: {
        paddingLeft: '16px',
      },
      [theme.breakpoints.up('md')]: {
        paddingRight: '16px',
      },
    },
  },
  mobileDropdownItems: {
    '& .FD-MuiListItemText-root': {
      paddingLeft: theme.spacing(2),
    },
  },
}));


export const NavButton = forwardRef(({
  value,
  className,
  label,
  dropdownItems,
  dropdownFallback,
  onClick,
  isDropdown,
  to,
  component,
  badge,
  ...rest
}, ref) => {
  const navColor = useBodyBackgroundColor();
  const classes = useStyles({ navColor });
  const btnRef = useRef(null);
  const {
    onOpen, onClose, onToggle, onNavClick, dropdownOpen,
  } = useNavButtonsContext(value);
  const { setShowMobileMenuFrom } = useLayoutContext();
  const mobile = useMobileLayout();

  if ((isDropdown && !dropdownItems && !dropdownFallback) || (!isDropdown && (dropdownItems || dropdownFallback))) {
    throwInDevOutsideReact('isDropdown must be used if and only if dropdownItems or dropdownFallback are specified');
  }


  const dropdownContent = (
    <div className={classNames(classes.dropdownItems, { [classes.mobileDropdownItems]: mobile })}>
      <FDPaper
        // This FDPaper is used so that in dark mode, the background of this dropdown will be dark
        overflow="hidden"
        appearance="elevated"
        data-test={`nav-button-${value}-dropdown`}
      >
        {dropdownItems && dropdownItems.length > 0 ? (
          <FDList disablePadding>
            {dropdownItems.map(item => (
              <FDListItem
                key={item.key}
                {...item}
                href={item.to}
                data-test={`nav-button-${value}-dropdown-${item.key}`}
                onClick={!item.onClick && !item.to ? undefined : () => {
                  onNavClick();
                  if (item.onClick) item.onClick();
                  // If you're in the mobile nav menu and you tap something else, close the menu.
                  // You can't achieve this effect by looking for URL changes for example, because the URL doesn't change if you open the menu and tap the same page you're on
                  if (mobile) setShowMobileMenuFrom(null);
                }}
              >
                {item.avatar ? <FDListItemAvatar src={item.avatar} alt="" /> : null}
                <FDListItemText primaryText={item.primaryText} secondaryText={item.secondaryText} />
                {item.lozenge ? <FDListItemSecondary>{item.lozenge}</FDListItemSecondary> : null}
              </FDListItem>
            ))}
          </FDList>
        ) : dropdownFallback}
      </FDPaper>
    </div>
  );

  return (
    <>
      <Button
        {...rest}
        aria-labelledby={isDropdown ? `nav-button-popper-for-${value}` : undefined}
        className={classNames(classes.root, {
          [classes.forceHoverLook]: dropdownOpen,
          [classes.rootDesktop]: !mobile,
        }, className)}
        component={to ? Link : component}
        to={to}
        ref={(el) => {
          btnRef.current = el;
          if (ref && typeof ref === 'function') ref(el);
        }}
        onClick={(e) => {
          if (onClick) onClick(e);
          // If you're in the mobile nav menu and you tap on a dropdown, expand that dropdown.
          if (mobile && isDropdown) onToggle();
          // If you're in the mobile nav menu and you tap something else, close the menu.
          // You can't achieve this effect by looking for URL changes for example, because the URL doesn't change if you open the menu and tap the same page you're on
          if (mobile && !isDropdown) setShowMobileMenuFrom(null);
        }}
        onMouseEnter={mobile ? undefined : onOpen}
        onMouseLeave={mobile ? undefined : onClose}
        disableRipple
        data-test={`nav-button-${value}`}
        data-selected={(dropdownOpen).toString()}
      >
        {label}
        {badge && badge.content ? (
          <Badge standalone badgeContent={badge.content} test={badge.test} className={classes.badge} />
        ) : null}
        {isDropdown ? (
          <span className={classNames(classes.expansionIcon, { [classes.flip]: dropdownOpen })} data-test="expansion-icon">
            <FDIcon icon="arrowDropDown" color="primary" />
          </span>
        ) : null}
      </Button>
      {mobile && dropdownOpen && dropdownContent}
      {isDropdown && !mobile && (
        <Popper
          id={`nav-button-popper-for-${value}`}
          open={dropdownOpen}
          anchorEl={btnRef?.current}
          onClose={onClose}
          keepMounted
          placement="bottom-start"
          className={classes.popperContainer}
        >
          <div onMouseEnter={onOpen} onMouseLeave={onClose}>
            {dropdownContent}
          </div>
        </Popper>
      )}
    </>
  );
});

NavButton.defaultProps = {
  className: undefined,
  component: undefined,
  dropdownItems: undefined,
  dropdownFallback: null,
  isDropdown: false,
  onClick: undefined,
  to: undefined,
  badge: undefined,
  lozenge: null,
};
NavButton.propTypes = {
  value: PropTypes.string.isRequired,
  className: PropTypes.string,
  component: PropTypes.string,
  label: PropTypes.node,
  dropdownItems: PropTypes.array,
  dropdownFallback: PropTypes.node,
  isDropdown: PropTypes.bool,
  onClick: PropTypes.func,
  to: PropTypes.string,
  badge: PropTypes.shape({
    content: PropTypes.node.isRequired,
    test: PropTypes.string,
  }),
  lozenge: PropTypes.node,
};
